<script lang="ts">
	import { dayjs } from '@woltair/translations-package-fe'
	import { Breadcrumbs } from '$lib/modules/blog'
	import { DOCUMENTS, WOLTADVISOR } from '$lib/routes'
	import { t, tTag } from '$lib/translations'
	import { Anchor } from '$lib/ui-woltair/components'
	import { PUBLIC_WOLTAIR_WEB_CODE } from '$env/static/public'

	type Article = {
		__typename?: string
		title?: string
		slug?: string
		publishDate?: string
		createdAt?: string
		metaTitle?: string
		metaDescription?: string
		legacy?: true
		type?: 'blog'
		originalCategory?: 'fotovoltaika'
		author?: {
			data: {
				attributes: {
					name: string
					description: string
					photo: {
						data: {
							attributes: {
								url: string
							}
						}
					}
				}
			}
		}
		content?: {
			__typename: string
			id: string
			text: string
		}[]
		categories?: {
			data: {
				attributes: {
					title: string
					slug: string
				}
			}[]
		}
		mainImage?: {
			data: {
				attributes: {
					url: string
				}
			}[]
		}
	}

	export let origin = ''
	export let path = ''
	export let article: Article = {}
	export let showBreadcrumbs = false
	export let CTA = false
	export let referer: boolean | null = null

	const dateFormat = {
		WOLTAIR_CZ: 'D. MMMM YYYY',
		WOLTAIR_PL: 'D.MM.YYYY',
		WOLTAIR_DE: 'D. MMMM YYYY',
		WOLTAIR_IT: 'D MMMM YYYY'
	}

	const { origin: imgOrigin, pathname: imgPathname } = article?.mainImage?.data?.[0]?.attributes
		?.url
		? new URL(article.mainImage.data[0].attributes.url)
		: {}

	const imgUrlResized =
		imgOrigin && imgPathname
			? `${imgOrigin}/${imgPathname
					.split('/')
					.map((part, i, arr) => {
						if (part === 'public' && i === arr.length - 1) return 'width=800'
						return part
					})
					.join('/')}`
			: null

	const author = article?.author?.data?.attributes
</script>

{#if article}
	{@const displayTitle = article.title
		.split(' ')
		.map((word) => `<span class="py-[0.05em] mx-[.1em] inline-block">${word}</span>`)
		.join('')}

	<div class="blog-section px-2" itemscope itemtype="https://schema.org/Article">
		<meta itemprop="mainEntityOfPage" content="{origin}{path}" />
		<meta
			content={dayjs(article.publishDate || article.createdAt).format('YYYY-MM-DD')}
			itemprop="datePublished"
		/>

		{#if article.categories.data?.length && !article.categories?.data[0].attributes?.slug?.includes(DOCUMENTS) && showBreadcrumbs}
			<Breadcrumbs category={article.categories?.data[0].attributes} />
		{/if}

		<div class="blog-wrapper mx-auto mt-3 w-full max-w-[950px]">
			<div class="heading-cont">
				<Anchor padded id="blog" />
				{#if referer}
					<h1 class="bg-white">
						{@html displayTitle}
					</h1>
				{:else}
					<h2 class="h1 bg-white">{@html article.title}</h2>
				{/if}
				<meta itemprop="headline" content={article.title} />
				<div itemprop="publisher" itemscope itemtype="https://schema.org/Organization">
					<div itemprop="logo" itemscope itemtype="https://schema.org/ImageObject">
						<meta itemprop="url" content="{origin}/images/logos/woltair.svg" />
					</div>
					<meta itemprop="name" content="Woltair" />
				</div>
				{#if author}
					<div
						class="author-tag my-4 flex items-center gap-4"
						itemprop="author"
						itemscope
						itemtype="https://schema.org/Person"
					>
						{#if author?.photo?.data?.attributes?.url}
							<img
								itemprop="image"
								class="h-16 w-auto"
								src={author?.photo?.data?.attributes?.url}
								alt={author.name}
							/>
						{/if}
						<div class="text-xs text-neutral-500">
							<p class="unstyled !mb-0" itemprop="name">{author?.name}</p>
							<p class="unstyled !mb-0" itemprop="description">
								{author.description}
							</p>
							<p class="unstyled !mb-0">
								{dayjs(article.publishDate || article.createdAt).format(
									dateFormat[PUBLIC_WOLTAIR_WEB_CODE]
								)}
							</p>
						</div>
					</div>
				{/if}
			</div>
			<div class="blog-cont h-fit max-w-full overflow-y-visible">
				<div class="main-img-cont">
					{#if imgUrlResized}
						<img itemprop="image" src={imgUrlResized} alt={article.title} />
					{:else}
						<img
							itemprop="image"
							src="/images/illustrations/woltair-house.svg"
							alt={tTag('web.components.header.illustration.imgAlt')}
						/>
					{/if}
				</div>
				<div class="text-wrap" itemprop="articleBody">
					{@html article.content[0].text}
				</div>
				{#if CTA}
					<section class="column-xl gap-15 mt-10 flex flex-col md:flex-row">
						<h2 class="!m-0">
							{t('web.blog.cta.title')}
						</h2>
						<div class="mt-8">
							<div>
								<a href="/{WOLTADVISOR}" class="btn btn-filled-primary unstyled !text-white">
									<img src="/images/icons/icon-calculator.svg" alt="Kalkulačka" />
									{t('web.blog.cta.calc')}
								</a>
							</div>
							{#if PUBLIC_WOLTAIR_WEB_CODE === 'WOLTAIR_CZ'}
								<!-- temporary hidden, this needs to be fixed! -->
								<div class="mt-3">
									<a href="/schuzka" class="btn btn-outline-accent">
										<img src="/images/avatars/team.webp" alt="Technici" />
										{t('web.blog.cta.expert')}
									</a>
								</div>
							{/if}
						</div>
					</section>
				{/if}
			</div>
		</div>
	</div>
{/if}
